/**
 * Recording a round: the bridge between the ruleset and the stored game.
 *
 * Note that settings are read from the game snapshot, not from live app state.
 * Changing a toggle mid-game must never rescore rounds already played.
 */

import { getRuleset } from '../rules';
import { describeRejection } from '../rules/rejection';
import type { DrawContext, HandInput, PlayerId, ScoreResult, WinContext } from '../rules/types';
import { editDrawTenpai, editWinHand, markEdited } from './editRound';
import type { Game, RoundRecord } from './types';

export interface RecordWinResult {
  round: RoundRecord;
  /** Problems that block recording. Non-empty means nothing was recorded. */
  problems: string[];
}

export function recordWin(
  game: Game,
  hand: HandInput,
  ctx: WinContext,
  photos?: string[],
): RecordWinResult {
  const ruleset = getRuleset(game.rulesetId);
  const settings = game.settings;

  // Backstop only — the play screen never offers the round form while the
  // seating is pending, and guests are held back too. Without the seating the
  // dealer (and every seat wind) is unknown, so a scored round would be wrong.
  if (game.seatingPending) {
    return { round: emptyRound(game, ctx), problems: ['Set the seating before recording a round.'] };
  }

  const problems = ruleset.validate(hand, ctx, settings);
  if (problems.length > 0) {
    return { round: emptyRound(game, ctx), problems };
  }

  const result = ruleset.score(hand, ctx, settings);
  if (!result.meetsMinimum) {
    return { round: emptyRound(game, ctx), problems: [describeRejection(result)] };
  }

  const players = game.players.map((p) => p.id);
  const payments = ruleset.settle(result, ctx, players, settings);

  const round: RoundRecord = {
    index: game.rounds.length + 1,
    outcome: 'win',
    dealer: ctx.dealer,
    prevailingWind: ctx.prevailingWind,
    winner: ctx.winner,
    winningTile: hand.winningTile,
    selfDraw: ctx.selfDraw,
    ...(ctx.discarder ? { discarder: ctx.discarder } : {}),
    fan: result.fan,
    effectiveFan: result.effectiveFan,
    basePoints: result.basepoints,
    patterns: result.patterns,
    isLimit: result.isLimit,
    ...(photos?.length ? { photos } : {}),
    concealed: hand.concealed,
    melds: hand.melds,
    flowers: hand.flowers ?? [],
    ...(hand.claimedSet ? { claimedSet: true } : {}),
    payments,
    recordedAt: Date.now(),
  };

  game.rounds.push(round);
  return { round, problems: [] };
}

export function recordDraw(game: Game, ctx: DrawContext): RoundRecord {
  if (game.seatingPending) throw new Error('Set the seating before recording a round.');
  const ruleset = getRuleset(game.rulesetId);
  const players = game.players.map((p) => p.id);
  const round: RoundRecord = {
    index: game.rounds.length + 1,
    outcome: 'draw',
    dealer: ctx.dealer,
    prevailingWind: game.rounds.at(-1)?.prevailingWind ?? 'east',
    tenpai: ctx.tenpai,
    payments: ruleset.settleDraw(ctx, players, game.settings),
    recordedAt: Date.now(),
  };
  game.rounds.push(round);
  return round;
}

export interface EditWinParticipants {
  winner: PlayerId;
  /** Required when the round was not a self-draw. */
  discarder?: PlayerId;
}

/**
 * Corrects who was involved in the most recently recorded win — a mis-tapped
 * winner or discarder, not the hand itself. The tile shape didn't change, so
 * this reuses the already-computed fan/pattern breakdown and only
 * recomputes payments for the corrected participants.
 *
 * Self-draw status is deliberately NOT editable here: self-draw itself earns
 * bonus fan in this ruleset, so flipping it would need the hand re-scored,
 * and this function only recomputes payments from the already-computed
 * result. `RoundRecord` does now carry `concealed`/`melds` (kept for audit
 * trail and hand-picture rendering), so a real re-score is possible in
 * principle — but wiring that up here is a deliberate non-goal for now.
 * Reusing the old fan while changing self-draw would silently produce wrong
 * points, which is worse than not supporting the correction at all. If
 * self-draw itself was wrong, the round needs to be re-entered from the
 * hand, not patched here.
 *
 * Last round only (a quick who-was-involved fix). To correct anything on an
 * EARLIER round — or the hand itself — use `editWinHand` / `editDrawTenpai`
 * in `./editRound`, which explains why that is safe. Like every correction it
 * leaves an "edited" trail holding the original result.
 */
export function editLastWinParticipants(game: Game, participants: EditWinParticipants): RoundRecord {
  const last = game.rounds.at(-1);
  if (!last || last.outcome !== 'win') {
    throw new Error('The last round is not a recorded win');
  }
  if (!last.selfDraw && !participants.discarder) {
    throw new Error('Discard win requires a discarder');
  }

  const ruleset = getRuleset(game.rulesetId);
  const players = game.players.map((p) => p.id);

  const result: ScoreResult = {
    fan: last.fan ?? 0,
    effectiveFan: last.effectiveFan ?? 0,
    patterns: last.patterns ?? [],
    isLimit: last.isLimit ?? false,
    basepoints: last.basePoints ?? 0,
    meetsMinimum: true,
    notes: [],
  };

  const ctx: WinContext = {
    winner: participants.winner,
    // seatWind is only consulted by score(), never by settle() — the hand
    // is already scored, so this is inert; kept only to satisfy the type.
    seatWind: last.prevailingWind,
    prevailingWind: last.prevailingWind,
    dealer: last.dealer,
    selfDraw: last.selfDraw ?? false,
    ...(last.selfDraw ? {} : { discarder: participants.discarder }),
  };

  const payments = ruleset.settle(result, ctx, players, game.settings);

  // Nothing actually changed: don't mark the round as edited.
  if (participants.winner === last.winner && (last.selfDraw || participants.discarder === last.discarder)) {
    return last;
  }

  const updated: RoundRecord = markEdited(last, {
    ...last,
    winner: participants.winner,
    ...(last.selfDraw ? {} : { discarder: participants.discarder }),
    payments,
  });

  game.rounds[game.rounds.length - 1] = updated;
  return updated;
}

/**
 * Full re-score of the most recently recorded win: the tile shape itself
 * (concealed/melds/flowers/winning tile), not just who was involved. Runs
 * the same validate -> score -> settle pipeline as a fresh `recordWin`, so
 * it's held to the same correctness bar — a bad edit is rejected the same
 * way a bad fresh entry would be.
 *
 * The round's own stored dealer and prevailing wind are used, not whatever
 * `ctx` carries: they belong to the round as it was played, and must not drift
 * just because a correction is being made.
 *
 * The most recent round may also change its WINNER (nothing comes after it).
 * For any round, last or earlier, see `editWinHand` in `./editRound`.
 */
export function editLastWinHand(
  game: Game,
  hand: HandInput,
  ctx: WinContext,
  photos?: string[],
): RecordWinResult {
  const last = game.rounds.at(-1);
  if (!last || last.outcome !== 'win') {
    throw new Error('The last round is not a recorded win');
  }
  const { round, problems } = editWinHand(game, last.index, hand, ctx, photos);
  return { round, problems };
}

/** Same correction, for a drawn round's tenpai list (last round; see `editDrawTenpai` for any round). */
export function editLastDrawTenpai(game: Game, tenpai: PlayerId[]): RoundRecord {
  const last = game.rounds.at(-1);
  if (!last || last.outcome !== 'draw') {
    throw new Error('The last round is not a recorded draw');
  }
  const { round, problems } = editDrawTenpai(game, last.index, tenpai);
  if (problems.length > 0) throw new Error(problems.join(' '));
  return round;
}

/**
 * Deletes the most recently recorded round outright — for an accidental
 * duplicate submission (e.g. a guest's network retry that slipped past the
 * `clientSubmissionId` idempotency check because it came from a fresh page
 * load), not a scoring mistake, which `editLastWinHand` covers instead.
 *
 * Only the last round is ever deletable: removing a round shifts the
 * dealer / round-wind rotation and the index of every round after it, and each
 * later round's stored dealer, wind and seat-wind fan were computed under the
 * rotation that no longer happened. (Correcting a round's CONTENT is a
 * different matter and is allowed on earlier rounds — see `./editRound`.)
 * To undo more than one round, call this repeatedly — each call removes what
 * is then the new last round, working backwards one at a time.
 */
export function deleteLastRound(game: Game): RoundRecord {
  const last = game.rounds.pop();
  if (!last) {
    throw new Error('There is no round to delete');
  }
  return last;
}

/** Ends the game. Idempotent. */
export function endGame(game: Game): Game {
  game.endedAt ??= Date.now();
  return game;
}

/**
 * Undoes an End Game — a safety net for confirming it by mistake. Every
 * round already recorded is untouched either way (ending a game never
 * deletes anything); this just clears the timestamp so the game is
 * in-progress again and lands back on the entry screen instead of the
 * summary. Whatever round was mid-entry when End Game was tapped was never
 * saved in the first place, so it still needs re-entering — same as it
 * would after any other navigation away from an unsaved form.
 */
export function reopenGame(game: Game): Game {
  game.endedAt = undefined;
  return game;
}

function emptyRound(game: Game, ctx: WinContext): RoundRecord {
  return {
    index: game.rounds.length + 1,
    outcome: 'win',
    dealer: ctx.dealer,
    prevailingWind: ctx.prevailingWind,
    payments: game.players.map((p) => ({ player: p.id, delta: 0 })),
    recordedAt: Date.now(),
  };
}
